import { targetParams, type ClientTarget } from '@features/clients';
import { apiRequest } from '@lib/api';

import {
    JobPage,
    JobResponse,
    JobsResponse,
    ModulesResponse,
    type BulkModule,
    type JobHeader,
} from '../schemas/bulk-migration';

/**
 * The Salesforce migration API, one function per route, aimed like the clients
 * API (`?env=` and, for a slot, `&slot=`): the runner, its jobs and the museums
 * all live in one deployment.
 *
 * No function here sends who is asking. The API stamps the session's email on
 * every write and refuses a body that tries.
 */

function withTarget(target: ClientTarget, extra: Record<string, string> = {}): string {
    return new URLSearchParams({ ...targetParams(target), ...extra }).toString();
}

function modulePath(moduleId: string): string {
    return `/bulk-migrations/modules/${encodeURIComponent(moduleId)}`;
}

function reportPath(moduleId: string, reportId: string): string {
    return `${modulePath(moduleId)}/reports/${encodeURIComponent(reportId)}`;
}

export async function listBulkModules(
    target: ClientTarget,
    signal?: AbortSignal,
): Promise<BulkModule[]> {
    const { modules } = await apiRequest(
        `/bulk-migrations/modules?${withTarget(target)}`,
        ModulesResponse,
        { signal },
    );
    return modules;
}

/** The most jobs one listing returns: the API's own cap (newest first, no cursor). */
export const MAX_JOBS_LISTED = 50;

export async function listBulkJobs(
    target: ClientTarget,
    moduleId: string,
    signal?: AbortSignal,
    limit = 20,
): Promise<JobHeader[]> {
    const { jobs } = await apiRequest(
        `${modulePath(moduleId)}/reports?${withTarget(target, { limit: String(limit) })}`,
        JobsResponse,
        { signal },
    );
    return jobs;
}

export interface JobPageRequest {
    readonly cursor?: string | undefined;
    readonly limit?: number | undefined;
    readonly outcome?: string | undefined;
}

export function getBulkJob(
    target: ClientTarget,
    moduleId: string,
    reportId: string,
    page: JobPageRequest = {},
    signal?: AbortSignal,
): Promise<JobPage> {
    const extra: Record<string, string> = {};
    if (page.limit !== undefined) extra.limit = String(page.limit);
    if (page.cursor !== undefined) extra.cursor = page.cursor;
    if (page.outcome !== undefined) extra.outcome = page.outcome;

    return apiRequest(`${reportPath(moduleId, reportId)}?${withTarget(target, extra)}`, JobPage, {
        signal,
    });
}

export interface DryRunInput {
    readonly tenantIds?: readonly string[] | undefined;
    readonly excludeSandboxes: boolean;
    readonly reason?: string | undefined;
}

export async function startBulkDryRun(
    target: ClientTarget,
    moduleId: string,
    input: DryRunInput,
): Promise<JobHeader> {
    const body = {
        excludeSandboxes: input.excludeSandboxes,
        ...(input.tenantIds !== undefined ? { tenantIds: input.tenantIds } : {}),
        ...(input.reason ? { reason: input.reason } : {}),
    };
    const { job } = await apiRequest(
        `${modulePath(moduleId)}/dry-runs?${withTarget(target)}`,
        JobResponse,
        { method: 'POST', body },
    );
    return job;
}

export interface LaunchInput {
    readonly reason: string;
    readonly tenantIds: readonly string[];
    /** Sent only as `true` (see `launchInput`); absent is a plain launch. */
    readonly resync?: true;
}

export async function launchBulkMigration(
    target: ClientTarget,
    moduleId: string,
    dryRunReportId: string,
    input: LaunchInput,
): Promise<JobHeader> {
    const { job } = await apiRequest(
        `${reportPath(moduleId, dryRunReportId)}/launch?${withTarget(target)}`,
        JobResponse,
        { method: 'POST', body: input },
    );
    return job;
}

export async function refreshBulkLaunch(
    target: ClientTarget,
    moduleId: string,
    launchReportId: string,
): Promise<JobHeader> {
    const { job } = await apiRequest(
        `${reportPath(moduleId, launchReportId)}/refresh?${withTarget(target)}`,
        JobResponse,
        // An empty body, not none: CloudFront's origin signing wants a payload hash
        // on a POST, and `apiRequest` only sends one with a body (see `lib/api.ts`).
        { method: 'POST', body: {} },
    );
    return job;
}
